/**
 * activityApi — Sprint 9 / neue Backend-API
 *
 * Typed API-Calls für GET /api/v2/activities.
 * Neues Shape: data: ActivityGroup[], meta mit has_more.
 * Filter-Params: action_type (= kind), since, until, hotspot_id, page, per_page.
 */

import i18next from '../../../i18n'
import apiClient from '../../../lib/api-client'

// --- Typen (neue gruppierte API-Shape) ---

export type ActivityGroupHotspot = {
  id: number
  name: string | null
  /** Geraetetyp (iot | wifi | cbrs | ...), null wenn dem BE unbekannt. */
  device_type?: string | null
}

// --- Detailfelder je Ereignistyp (R5 / Stream FD) ---
//
// Das Backend liefert die Rohwerte aus activities.rewards als `details`-Objekt
// (Activity::DetailService). Uebersetzbare Statuscodes kommen als *_key-Slug;
// die englischen *_label-Rohstrings sind nur Fallback und gehoeren nicht
// ungeprueft ins UI (i18n-Vorgabe).

export type HeartbeatDetails = {
  validity: number | null
  validity_key: string
  validity_label?: string | null
  lat: number | null
  lon: number | null
  /** null wenn 0 — v1 blendet die Zeile dann aus. */
  distance_to_asserted_m: number | null
  location_trust_score_multiplier: number | null
  reward_multiplier: number | null
  cell_type: string | null
}

export type SpeedtestDetails = {
  result: number | null
  result_key: string
  result_label?: string | null
  upload_speed_bps: number | null
  download_speed_bps: number | null
  latency_ms: number | null
  serial: string | null
}

export type OnlineDetails = {
  status: 'online' | 'offline' | 'listen_addr_change'
  listen_addrs: string | string[] | null
  status_changed_at: string | null
}

export type DataDetails = {
  /** BE-Feldname bewusst abweichend von v1 ("Data Packets"): der Wert ist die Payload-Groesse in Bytes. */
  payload_size_bytes: number | null
  num_dcs: number | null
}

export type RewardBreakdownEntry = {
  type: string
  human_name: string
  color: string | null
  count: number
  amount: number
}

export type RewardBreakdownDetails = {
  breakdown: RewardBreakdownEntry[]
}

export type ActivityDetails =
  | HeartbeatDetails
  | SpeedtestDetails
  | OnlineDetails
  | DataDetails
  | RewardBreakdownDetails

export type ActivityGroupOrigin = {
  beaconer_name?: string | null
  city?: string | null
  transmit_scale?: number | null
  valid_witness_count?: number | null
  invalid_witness_count?: number | null
  distance?: number | null
  rssi?: number | null
  [key: string]: unknown
}

export type ActivityGroup = {
  group_key: string
  kind: string
  human_label: string
  color: string
  time: number
  occurred_at: string
  hotspots: ActivityGroupHotspot[]
  hotspot_count: number
  origin: ActivityGroupOrigin | null
  rewards_total: number
  token: 'hnt' | 'iot' | 'mobile' | 'dc' | null
  activity_count: number
  era: 'pre' | 'post'
  /** R5/FD: Ereignistyp-abhaengige Detailfelder, null wenn der Typ keine kennt. */
  details?: ActivityDetails | null
}

export type ActivitiesResponse = {
  data: ActivityGroup[]
  meta: {
    page: number
    per_page: number
    total: number
    /** R5/FD: Anzahl der auf dieser Seite ausgelieferten Gruppen (total zaehlt Aktivitaeten). */
    group_count?: number
    has_more: boolean
    /** Free/Premium-Zeitfenster in Tagen (Backend-Gating). */
    day_limit?: number
    /** Tatsaechlich angewendete untere Zeitgrenze (ISO8601) = max(since, jetzt - day_limit). */
    window_start?: string
  }
}

export type ActivitiesParams = {
  page?: number
  per_page?: number
  action_type?: string
  since?: string
  until?: string
  hotspot_id?: number
}

// --- Mock-Daten ---

/**
 * Baut die Mock-Gruppen zur Aufrufzeit (nicht beim Modul-Import), damit
 * `human_label` immer über i18next.t mit der aktuell aktiven Sprache
 * übersetzt wird (Fix-Muster für Non-React-Utilities, siehe hotspotPopupHtml.ts).
 */
function buildMockGroups(): ActivityGroup[] {
  return [
    {
      group_key: 'beacon-1-2026-05-28',
      kind: 'beacon',
      human_label: i18next.t('activity:mockLabels.beaconSent'),
      color: '#00bcd4',
      time: Date.now() - 2 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 1, name: 'Alpha-Node-7' }],
      hotspot_count: 1,
      origin: { beaconer_name: 'Alpha-Node-7', city: 'Berlin', transmit_scale: 1.0, valid_witness_count: 5, invalid_witness_count: 0, distance: 420, rssi: -90 },
      rewards_total: 0,
      token: null,
      activity_count: 1,
      era: 'post',
    },
    {
      group_key: 'witness-2-2026-05-28',
      kind: 'witness',
      human_label: i18next.t('activity:mockLabels.witnessConfirmed'),
      color: '#7c3aed',
      time: Date.now() - 5 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 1, name: 'Alpha-Node-7' }, { id: 2, name: 'Beta-Relay-3' }],
      hotspot_count: 2,
      origin: { beaconer_name: 'Gamma-Sentinel', city: 'Hamburg', transmit_scale: 0.8, valid_witness_count: 3, invalid_witness_count: 1, distance: 800, rssi: -100 },
      rewards_total: 0,
      token: null,
      activity_count: 2,
      era: 'post',
    },
    {
      group_key: 'iot_rewards-3-2026-05-27',
      kind: 'iot_rewards',
      human_label: i18next.t('activity:mockLabels.iotRewardsReceived'),
      color: '#22c55e',
      time: Date.now() - 30 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 1, name: 'Alpha-Node-7' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 12.45,
      token: 'iot',
      activity_count: 3,
      era: 'post',
      details: {
        breakdown: [
          { type: 'poc_witnesses', human_name: 'Witness', color: 'btn-warning', count: 3, amount: 0.0174 },
          { type: 'data_credits', human_name: 'Data', color: 'btn-info', count: 1, amount: 0.0021 },
        ],
      },
    },
    {
      group_key: 'act_9001',
      kind: 'heartbeat',
      human_label: i18next.t('activity:actionTypes.heartbeat'),
      color: '#a855f7',
      time: Date.now() - 31 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 31 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 1, name: 'Alpha-Node-7', device_type: 'wifi' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 0,
      token: null,
      activity_count: 1,
      era: 'post',
      details: {
        validity: 0,
        validity_key: 'valid',
        validity_label: 'Valid heartbeat',
        lat: 52.5163,
        lon: 13.3777,
        distance_to_asserted_m: 12,
        location_trust_score_multiplier: 1,
        reward_multiplier: 1,
        cell_type: 'nova_generic_wifi_outdoor',
      },
    },
    {
      group_key: 'act_9002',
      kind: 'speedtest',
      human_label: i18next.t('activity:actionTypes.speedtest'),
      color: '#a855f7',
      time: Date.now() - 32 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 32 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 1, name: 'Alpha-Node-7', device_type: 'wifi' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 0,
      token: null,
      activity_count: 1,
      era: 'post',
      details: {
        result: 0,
        result_key: 'speedtest_valid',
        result_label: 'speedtest_valid',
        upload_speed_bps: 12_500_000,
        download_speed_bps: 48_000_000,
        latency_ms: 31,
        serial: 'SN-0815',
      },
    },
    {
      group_key: 'act_9003',
      kind: 'online',
      human_label: i18next.t('activity:actionTypes.online'),
      color: '#22c55e',
      time: Date.now() - 33 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 33 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 2, name: 'Beta-Relay-3', device_type: 'iot' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 0,
      token: null,
      activity_count: 1,
      era: 'post',
      details: {
        status: 'online',
        listen_addrs: '/ip4/1.2.3.4/tcp/44158',
        status_changed_at: new Date(Date.now() - 33 * 60 * 60 * 1000).toISOString(),
      },
    },
    {
      group_key: 'act_9004',
      kind: 'data',
      human_label: i18next.t('activity:actionTypes.data'),
      color: '#38bdf8',
      time: Date.now() - 34 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 34 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 2, name: 'Beta-Relay-3', device_type: 'iot' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 0,
      token: 'dc',
      activity_count: 1,
      era: 'post',
      details: { payload_size_bytes: 24_576, num_dcs: 0 },
    },
    {
      group_key: 'payment-4-2026-05-26',
      kind: 'payment',
      human_label: i18next.t('activity:mockLabels.hntPayment'),
      color: '#f59e0b',
      time: Date.now() - 48 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
      hotspots: [],
      hotspot_count: 0,
      origin: null,
      rewards_total: 1.04,
      token: 'hnt',
      activity_count: 1,
      era: 'pre',
      details: {
        breakdown: [
          { type: 'poc_witnesses', human_name: 'Witness', color: 'btn-warning', count: 8, amount: 0.82 },
          { type: 'consensus', human_name: 'Consensus', color: 'btn-primary', count: 1, amount: 0.22 },
        ],
      },
    },
    {
      group_key: 'added-5-2026-05-25',
      kind: 'added',
      human_label: i18next.t('activity:mockLabels.hotspotAdded'),
      color: '#00bcd4',
      time: Date.now() - 72 * 60 * 60 * 1000,
      occurred_at: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
      hotspots: [{ id: 3, name: 'Delta-Gateway' }],
      hotspot_count: 1,
      origin: null,
      rewards_total: 0,
      token: null,
      activity_count: 1,
      era: 'post',
    },
  ]
}

function mockFetch(params: ActivitiesParams): ActivitiesResponse {
  const perPage = params.per_page ?? 20
  const page = params.page ?? 1

  let filtered = buildMockGroups()
  if (params.action_type) {
    // action_type akzeptiert seit R5 auch eine Komma-Liste ("heartbeat,speedtest").
    const wanted = new Set(
      params.action_type.split(',').map((k) => k.trim()).filter(Boolean),
    )
    filtered = filtered.filter((g) => wanted.has(g.kind))
  }

  const start = (page - 1) * perPage
  const end = start + perPage
  const slice = filtered.slice(start, end)

  return {
    data: slice,
    meta: {
      page,
      per_page: perPage,
      total: filtered.length,
      has_more: end < filtered.length,
    },
  }
}

// --- API-Funktion ---

export async function fetchActivities(
  params: ActivitiesParams,
): Promise<{ data?: ActivitiesResponse; error?: unknown; status?: number; fromMock?: boolean }> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const client = apiClient as any
    const query: Record<string, string | number> = {}
    if (params.page) query.page = params.page
    if (params.per_page) query.per_page = params.per_page
    if (params.action_type) query.action_type = params.action_type
    if (params.since) query.since = params.since
    if (params.until) query.until = params.until
    if (params.hotspot_id) query.hotspot_id = params.hotspot_id

    const result = await client.GET('/api/v2/activities', { params: { query } })

    if (result.response?.status === 404) {
      return { data: mockFetch(params), status: 404, fromMock: true }
    }

    return result as { data?: ActivitiesResponse; error?: unknown }
  } catch (err) {
    return { error: err }
  }
}

